import onePieceSetMeta from "../data/onePieceSetMeta";
import { getPriceDollars } from "./price";

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "webp", "avif"];

const DEFAULT_THEME = {
  accent: "#c7932d",
  chipBg: "rgba(255,255,255,0.18)",
  heroGradient: "linear-gradient(135deg, #0d1f33 0%, #173a5f 56%, #d48d34 100%)",
  cardBg: "#fbfaf7",
};

const THEME_BY_PREFIX = {
  OP: {
    accent: "#cf8b28",
    chipBg: "rgba(255,255,255,0.18)",
    heroGradient: "linear-gradient(135deg, #0c1d32 0%, #15436a 54%, #dc9536 100%)",
    cardBg: "#fbfaf7",
  },
  EB: {
    accent: "#b94f4b",
    chipBg: "rgba(255,255,255,0.18)",
    heroGradient: "linear-gradient(135deg, #231528 0%, #6d2f54 52%, #d17a47 100%)",
    cardBg: "#fcf8f8",
  },
  PRB: {
    accent: "#8b6cd9",
    chipBg: "rgba(255,255,255,0.2)",
    heroGradient: "linear-gradient(135deg, #1a1730 0%, #43397c 54%, #d6b45b 100%)",
    cardBg: "#faf9fe",
  },
  ST: {
    accent: "#2f8a77",
    chipBg: "rgba(255,255,255,0.18)",
    heroGradient: "linear-gradient(135deg, #11282b 0%, #1f5f57 52%, #d6a046 100%)",
    cardBg: "#f8fbfa",
  },
  P: {
    accent: "#d0a648",
    chipBg: "rgba(255,255,255,0.2)",
    heroGradient: "linear-gradient(135deg, #2b1f10 0%, #6f4f19 50%, #eccf7c 100%)",
    cardBg: "#fcfaf4",
  },
};

function normalizeOnePieceName(value) {
  return String(value || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function onePieceSetSlugFromName(name) {
  return String(name || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " and ")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
}

function getOnePieceCustomSlug(code, setName) {
  const normalizedCode = String(code || "").trim().toUpperCase();
  if (normalizedCode === "OP15") return "op15";
  if (normalizedCode === "OP16") return "op16";
  if (normalizedCode === "OP17") return "op17";
  if (normalizedCode === "EB04") return "eb04";
  return onePieceSetSlugFromName(setName);
}

function normalizeOnePieceLookup(value) {
  return String(value || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .trim();
}

function getOnePieceDisplayCode(code) {
  const normalizedCode = String(code || "").trim().toUpperCase();
  if (normalizedCode === "OP15") return "OP-15";
  if (normalizedCode === "OP16") return "OP-16";
  if (normalizedCode === "OP17") return "OP-17";
  if (normalizedCode === "EB04") return "EB-04";
  return normalizedCode;
}

function compactSetCode(value) {
  return String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

function inferOnePieceCodeFromText(value) {
  const raw = String(value || "")
    .toUpperCase()
    .replace(/\u2010|\u2011|\u2012|\u2013|\u2014/g, "-");
  if (!raw) return "";

  const codeMatch = raw.match(/\b(OP|EB|ST|PRB)\s*-?\s*(\d{2})\b/);
  if (codeMatch) return `${codeMatch[1]}${codeMatch[2]}`;
  if (/\bPROMO\b|\bPROMOTION\b/.test(raw)) return "P";
  return "";
}

function getOnePieceResolvedCode(entry) {
  return String(entry?.code || "").trim().toUpperCase() || inferOnePieceCodeFromText(entry?.set_name || "");
}

function getOnePieceSetCodePrefix(code) {
  const normalized = String(code || "").trim().toUpperCase();
  if (normalized.startsWith("PRB")) return "PRB";
  if (normalized.startsWith("OP")) return "OP";
  if (normalized.startsWith("EB")) return "EB";
  if (normalized.startsWith("ST")) return "ST";
  if (normalized === "P") return "P";
  return normalized.replace(/[0-9].*$/, "") || normalized;
}

export function isOnePiecePromoSet(entry) {
  const resolvedCode = getOnePieceResolvedCode(entry);
  const setName = String(entry?.set_name || "").trim();
  return (
    Boolean(entry?.promo)
    || resolvedCode === "P"
    || /\bpromo\b|\bpromotion\b/i.test(setName)
  );
}

export function isOnePieceDeckSet(entry) {
  const family = getOnePieceSetCodePrefix(getOnePieceResolvedCode(entry));
  const notes = String(entry?.notes || "").toLowerCase();
  const setName = String(entry?.set_name || "").toLowerCase();
  return family === "ST" || notes.includes("starter deck") || notes.includes("ultra deck") || setName.includes("starter deck") || setName.includes("ultra deck");
}

export function isOnePieceBoosterSet(entry) {
  const family = getOnePieceSetCodePrefix(entry?.code);
  return family === "EB" || family === "PRB";
}

export function getOnePieceBrowseGroup(entry) {
  if (isOnePieceDeckSet(entry)) return 3;
  if (isOnePiecePromoSet(entry)) return 2;
  if (isOnePieceBoosterSet(entry)) return 1;
  return 0;
}

function buildOnePieceCodeVariants(code) {
  const normalized = String(code || "").trim().toUpperCase();
  if (!normalized) return [];

  const match = normalized.match(/^([A-Z]+)(\d{1,2})$/);
  if (!match) return [normalized, normalized.toLowerCase()];

  const [, prefix, digits] = match;
  const twoDigit = digits.padStart(2, "0");
  const prefixLower = prefix.toLowerCase();

  return Array.from(new Set([
    `${prefix}${twoDigit}`,
    `${prefix}${twoDigit}`.toLowerCase(),
    `${prefix}-${twoDigit}`,
    `${prefixLower}-${twoDigit}`,
    `${prefixLower}${twoDigit}`,
  ]));
}

function buildOnePieceNameVariants(setName) {
  const raw = String(setName || "").trim();
  if (!raw) return [];

  const normalized = normalizeOnePieceName(raw);
  const slug = onePieceSetSlugFromName(raw);

  return Array.from(new Set([
    raw,
    raw.toLowerCase(),
    normalized,
    normalized.replace(/\s+/g, ""),
    slug,
    slug.replace(/-/g, " "),
  ].filter(Boolean)));
}

function buildOnePieceSlugAliases(code, setName, displayCode) {
  const normalizedCode = String(code || "").trim().toUpperCase();
  const codeSlug = normalizedCode.toLowerCase();
  const hyphenCodeSlug = normalizedCode
    .replace(/^([A-Z]+)(\d{2})$/, "$1-$2")
    .toLowerCase();
  const nameSlug = onePieceSetSlugFromName(setName);
  const normalizedNameSlug = normalizeOnePieceLookup(setName);
  const displaySlug = normalizeOnePieceLookup(displayCode);
  let shortNameSlug = "";
  if (normalizedCode === "OP15") {
    shortNameSlug = normalizeOnePieceLookup("Adventure on Kami's Island");
  } else if (normalizedCode === "OP16") {
    shortNameSlug = normalizeOnePieceLookup("The Time of Battle");
  } else if (normalizedCode === "OP17") {
    shortNameSlug = normalizeOnePieceLookup("The World's Strongest Warriors");
  }

  return Array.from(new Set([
    getOnePieceCustomSlug(normalizedCode, setName),
    codeSlug,
    hyphenCodeSlug,
    nameSlug,
    normalizedNameSlug,
    displaySlug,
    shortNameSlug,
    `${codeSlug}-${nameSlug}`,
    `${hyphenCodeSlug}-${nameSlug}`,
  ].filter(Boolean)));
}

const ONE_PIECE_SET_OVERRIDES = {
  OP15: {
    slug: "op15",
    code: "OP15",
    display_code: "OP-15",
    set_name: "Adventure on Kami's Island (OP15)",
    aliases: [
      "Adventure on Kami's Island",
      "Adventure on Kamis Island",
      "adventure-on-kamis-island",
      "adventure-on-kamis-island-op15",
      "op-15",
    ],
  },
  OP16: {
    slug: "op16",
    code: "OP16",
    display_code: "OP-16",
    set_name: "The Time of Battle",
    aliases: [
      "The Time of Battle",
      "the-time-of-battle",
      "the-time-of-battle-op16",
      "op-16",
    ],
  },
  OP17: {
    slug: "op17",
    code: "OP17",
    display_code: "OP-17",
    set_name: "The World's Strongest Warriors",
    aliases: ["The World's Strongest Warriors", "the-worlds-strongest-warriors", "the-worlds-strongest-warriors-op17", "op-17"],
  },
  EB04: {
    slug: "eb04",
    code: "EB04",
    display_code: "EB-04",
    set_name: "Extra Booster 04 (EB04)",
    aliases: [
      "Extra Booster 04",
      "extra-booster-04",
      "eb-04",
    ],
  },
};

function buildOnePieceSetAssetPath(fileName) {
  const text = String(fileName || "").trim();
  if (!text) return "";
  if (text.startsWith("/")) return text;
  return `/sets/one%20piece/${encodeURIComponent(text)}`;
}

function buildOnePieceSetAssetCandidate(baseName, extension) {
  const base = String(baseName || "").trim();
  const ext = String(extension || "").trim().replace(/^\./, "");
  if (!base || !ext) return "";
  return buildOnePieceSetAssetPath(`${base}.${ext}`);
}

const STARTER_DECK_IMAGE_BASES = {
  ST01: ["Starter Deck Straw Hat Crew"],
  ST02: ["Starter Deck Worst Generation"],
  ST03: ["Starter Deck The Seven Warlords of the Sea"],
  ST04: ["Starter Deck Animal Kingdom Pirates"],
  ST05: ["Starter Deck One Piece Film Edition"],
  ST06: ["Starter Deck Absolute Justice"],
  ST07: ["Starter Deck Big Mom Pirates"],
  ST08: ["Starter Deck Monkey.D.Luffy"],
  ST09: ["Starter Deck Yamato"],
  ST10: ["Starter Deck The Three Captains"],
  ST11: ["Starter Deck Uta"],
  ST12: ["Starter Deck Zoro and Sanji"],
  ST13: ["Starter Deck The Three Brothers"],
  ST14: ["Starter Deck 3D2Y"],
  ST15: ["Starter Deck Red Edward.Newgate"],
  ST16: ["Starter Deck Green Uta"],
  ST17: ["Starter Deck Blue Donquixote Doflamingo"],
  ST18: ["Starter Deck Purple Monkey.D.Luffy"],
  ST19: ["Starter Deck Black Smoker"],
  ST20: ["Starter Deck Yellow Charlotte Katakuri"],
};

export function getOnePieceSetImageCandidates(entry) {
  const candidates = [];
  const normalizedCode = getOnePieceResolvedCode(entry);
  const rawImageFile = String(entry?.imageFile || "").trim();
  const explicitImageFile = /\.pdf$/i.test(rawImageFile)
    ? ""
    : buildOnePieceSetAssetPath(rawImageFile);

  if (explicitImageFile) {
    candidates.push(explicitImageFile);
  }

  if (isOnePieceDeckSet(entry)) {
    const starterBases = Array.from(new Set([
      ...(STARTER_DECK_IMAGE_BASES[normalizedCode] || []),
      String(entry?.set_name || "").trim(),
      ...buildOnePieceNameVariants(entry?.set_name),
    ].filter(Boolean)));

    for (const base of starterBases) {
      for (const ext of IMAGE_EXTENSIONS) {
        candidates.push(buildOnePieceSetAssetCandidate(base, ext));
      }
    }
  }

  if (isOnePiecePromoSet(entry)) {
    candidates.push(buildOnePieceSetAssetPath("promo.jpg"));
    for (const ext of IMAGE_EXTENSIONS) {
      candidates.push(buildOnePieceSetAssetCandidate("promo", ext));
      candidates.push(buildOnePieceSetAssetCandidate("promos", ext));
    }
  }

  const explicitBases = [];
  if (normalizedCode === "OP15" || String(entry?.display_code || "").trim().toUpperCase() === "OP-15") {
    explicitBases.push("op-15", "op15", "OP-15", "OP15");
  }
  if (normalizedCode === "OP16" || String(entry?.display_code || "").trim().toUpperCase() === "OP-16") {
    explicitBases.push("op-16", "op16", "OP-16", "OP16");
  }
  if (normalizedCode === "OP17" || String(entry?.display_code || "").trim().toUpperCase() === "OP-17") {
    explicitBases.push("op17", "op-17", "OP17", "OP-17");
  }
  if (normalizedCode === "EB01") explicitBases.push("eb01", "EB01");
  if (normalizedCode === "EB02") explicitBases.push("eb02", "EB02");
  if (normalizedCode === "EB03") explicitBases.push("eb03", "EB03");
  if (normalizedCode === "PRB01") explicitBases.push("prb-01", "prb01", "PRB-01", "PRB01", "One Piece The Best");
  if (normalizedCode === "PRB02") explicitBases.push("prb-02", "prb02", "PRB-02", "PRB02", "One Piece Card The Best Vol.2");

  const baseNames = Array.from(new Set([
    ...explicitBases,
    ...buildOnePieceCodeVariants(entry?.code),
    ...buildOnePieceNameVariants(entry?.set_name),
  ]));

  for (const base of baseNames) {
    for (const ext of IMAGE_EXTENSIONS) {
      candidates.push(buildOnePieceSetAssetCandidate(base, ext));
    }
  }

  // Slug-based fallback for set_name (e.g., "Gift Collection 2023" → "/sets/one piece/gift-collection-2023.avif")
  const setName = entry?.set_name;
  if (setName) {
    const slug = onePieceSetSlugFromName(setName);
    if (slug) {
      const slugFormats = ["avif", "webp", "jpg"];
      for (const ext of slugFormats) {
        candidates.push(`/sets/one%20piece/${slug}.${ext}`);
      }
    }
  }

  return Array.from(new Set(candidates));
}

export function getOnePieceSetTheme(entry) {
  const prefix = getOnePieceSetCodePrefix(getOnePieceResolvedCode(entry));
  return THEME_BY_PREFIX[prefix] || DEFAULT_THEME;
}

const normalizedSets = [];
const bySlug = new Map();
const byCode = new Map();
const byName = new Map();
const supportedOnePieceSlugs = new Set();

Object.entries(onePieceSetMeta || {}).forEach(([code, meta], index) => {
  const normalizedCode = String(code || "").trim().toUpperCase();
  const override = ONE_PIECE_SET_OVERRIDES[normalizedCode] || null;
  const setName = String(override?.set_name || meta?.set_name || "").trim();
  const slug = String(override?.slug || getOnePieceCustomSlug(code, setName)).trim().toLowerCase();
  const displayCode = String(override?.display_code || getOnePieceDisplayCode(code)).trim();
  const aliases = Array.from(new Set([
    ...(Array.isArray(meta?.aliases) ? meta.aliases : []),
    ...(Array.isArray(override?.aliases) ? override.aliases : []),
  ].map((value) => String(value || "").trim()).filter(Boolean)));
  const entry = {
    ...meta,
    ...override,
    code: normalizedCode,
    display_code: displayCode,
    slug,
    set_name: setName,
    aliases,
    displayOrder: index,
    family: getOnePieceSetCodePrefix(normalizedCode),
  };

  normalizedSets.push(entry);
  byCode.set(normalizedCode, entry);
  byName.set(normalizeOnePieceLookup(setName), entry);
  buildOnePieceSlugAliases(normalizedCode, setName, entry.display_code).forEach((aliasSlug) => {
    const normalizedAlias = normalizeOnePieceLookup(aliasSlug);
    if (!normalizedAlias) return;
    supportedOnePieceSlugs.add(normalizedAlias);
    if (!bySlug.has(normalizedAlias)) {
      bySlug.set(normalizedAlias, entry);
    }
  });
  aliases.forEach((alias) => {
    const normalizedAlias = normalizeOnePieceLookup(alias);
    if (!normalizedAlias) return;
    supportedOnePieceSlugs.add(normalizedAlias);
    byName.set(normalizedAlias, entry);
    if (!bySlug.has(normalizedAlias)) {
      bySlug.set(normalizedAlias, entry);
    }
  });
});

export const onePieceSets = normalizedSets;
export function isBrowsableOnePieceSet(entry) {
  return String(entry?.code || "").trim().toUpperCase() !== "EB04";
}

export const browsableOnePieceSets = normalizedSets
  .filter((entry) => isBrowsableOnePieceSet(entry))
  .sort((a, b) => {
    const yearDiff = Number(b?.year || 0) - Number(a?.year || 0);
    if (yearDiff !== 0) return yearDiff;
    return Number(b?.displayOrder || 0) - Number(a?.displayOrder || 0);
  });

export function getOnePieceSetBySlug(slug) {
  const raw = String(slug || "").trim();
  if (!raw) return null;

  const normalizedLookup = normalizeOnePieceLookup(raw);
  const compactLookup = compactSetCode(raw).toUpperCase();
  if (!normalizedLookup && !compactLookup) return null;

  const exactSlugMatch = normalizedSets.find((entry) => normalizeOnePieceLookup(entry?.slug) === normalizedLookup);
  if (exactSlugMatch) return exactSlugMatch;

  const compactCodeMatch = normalizedSets.find((entry) => {
    const entryCode = compactSetCode(entry?.code).toUpperCase();
    const entryDisplayCode = compactSetCode(entry?.display_code).toUpperCase();
    return compactLookup && (entryCode === compactLookup || entryDisplayCode === compactLookup);
  });
  if (compactCodeMatch) return compactCodeMatch;

  const normalizedNameMatch = normalizedSets.find((entry) => normalizeOnePieceLookup(entry?.set_name) === normalizedLookup);
  if (normalizedNameMatch) return normalizedNameMatch;

  const aliasMatch = normalizedSets.find((entry) => (
    Array.isArray(entry?.aliases)
      && entry.aliases.some((alias) => normalizeOnePieceLookup(alias) === normalizedLookup)
  ));
  if (aliasMatch) return aliasMatch;

  const codeVariantMatch = normalizedSets.find((entry) => {
    const variants = [
      entry?.display_code,
      entry?.code,
      ...buildOnePieceCodeVariants(entry?.code),
    ];
    return variants.some((value) => (
      normalizeOnePieceLookup(value) === normalizedLookup
      || compactSetCode(value).toUpperCase() === compactLookup
    ));
  });
  if (codeVariantMatch) return codeVariantMatch;

  return bySlug.get(normalizedLookup) || byName.get(normalizedLookup) || null;
}

export const onePieceSupportedSlugs = Array.from(supportedOnePieceSlugs).sort((a, b) => a.localeCompare(b));

export function sortOnePieceSets(sets, sortBy) {
  const ordered = [...sets];
  if (sortBy === "name_asc") return ordered.sort((a, b) => compareDisplayOrder(a, b, "name_asc"));
  if (sortBy === "name_desc") return ordered.sort((a, b) => compareDisplayOrder(a, b, "name_desc"));
  if (sortBy === "year_asc") return ordered.sort((a, b) => compareDisplayOrder(a, b, "year_asc"));
  return ordered.sort((a, b) => compareDisplayOrder(a, b, "year_desc"));
}

function getOnePieceNumericCode(entry) {
  const resolvedCode = getOnePieceResolvedCode(entry);
  const match = resolvedCode.match(/^(OP|EB|PRB|ST)(\d{2})$/);
  if (!match) return null;
  return Number(match[2]);
}

function getOnePieceSetFamilyRank(entry) {
  if (isOnePiecePromoSet(entry)) return 0;
  const prefix = getOnePieceSetCodePrefix(getOnePieceResolvedCode(entry));
  if (prefix === "OP") return 1;
  if (prefix === "EB") return 2;
  if (prefix === "PRB") return 3;
  if (prefix === "ST") return 4;
  return 5;
}

export function getOnePieceSetSection(entry) {
  return isOnePieceDeckSet(entry) ? "starter_decks" : "sets";
}

export function getOnePieceSetDisplayOrder(entry, sortBy) {
  const safeSort = String(sortBy || "year_desc").trim().toLowerCase();
  const numericCode = getOnePieceNumericCode(entry);
  const yearValue = Number(entry?.year || 0);
  const setName = String(entry?.set_name || "");
  const familyRank = getOnePieceSetFamilyRank(entry);
  const promoRank = isOnePiecePromoSet(entry) ? 1 : 0;
  const starterRank = getOnePieceSetSection(entry) === "starter_decks" ? 1 : 0;

  if (safeSort === "name_asc") {
    return {
      starterRank,
      promoRank,
      primary: setName.toLowerCase(),
      secondary: numericCode ?? Number.MAX_SAFE_INTEGER,
      familyRank,
    };
  }

  if (safeSort === "name_desc") {
    return {
      starterRank,
      promoRank,
      primary: setName.toLowerCase(),
      secondary: -(numericCode ?? -1),
      familyRank,
    };
  }

  if (starterRank) {
    return {
      starterRank,
      promoRank,
      primary: safeSort === "year_asc" ? (numericCode ?? Number.MAX_SAFE_INTEGER) : -(numericCode ?? -1),
      secondary: safeSort === "year_asc" ? yearValue : -yearValue,
      familyRank,
    };
  }

  return {
    starterRank,
    promoRank,
    primary: safeSort === "year_asc" ? yearValue : -yearValue,
    secondary: safeSort === "year_asc" ? (numericCode ?? Number.MAX_SAFE_INTEGER) : -(numericCode ?? -1),
    familyRank,
  };
}

function compareDisplayOrder(left, right, sortBy) {
  const a = getOnePieceSetDisplayOrder(left, sortBy);
  const b = getOnePieceSetDisplayOrder(right, sortBy);

  if (a.starterRank !== b.starterRank) return a.starterRank - b.starterRank;
  if (a.promoRank !== b.promoRank) return a.promoRank - b.promoRank;

  if (typeof a.primary === "string" && typeof b.primary === "string") {
    const direction = String(sortBy || "year_desc").trim().toLowerCase() === "name_desc" ? -1 : 1;
    const primaryCompare = a.primary.localeCompare(b.primary) * direction;
    if (primaryCompare !== 0) return primaryCompare;
  } else if (a.primary !== b.primary) {
    return Number(a.primary) - Number(b.primary);
  }

  if (a.secondary !== b.secondary) {
    return Number(a.secondary) - Number(b.secondary);
  }
  if (a.familyRank !== b.familyRank) {
    return a.familyRank - b.familyRank;
  }
  return String(left?.set_name || "").localeCompare(String(right?.set_name || ""));
}

export function splitOnePieceSetsIntoSections(entries, sortBy) {
  const sections = {
    sets: [],
    starter_decks: [],
  };

  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    const section = getOnePieceSetSection(entry);
    if (section === "starter_decks") {
      sections.starter_decks.push(entry);
      return;
    }
    sections.sets.push(entry);
  });

  sections.sets.sort((a, b) => compareDisplayOrder(a, b, sortBy));
  sections.starter_decks.sort((a, b) => compareDisplayOrder(a, b, sortBy));

  return sections;
}

export function sortOnePieceSetItems(items, sortBy) {
  const sorted = Array.isArray(items) ? [...items] : [];

  const compareItemIdAsc = (a, b) => Number(a?.item_id || 0) - Number(b?.item_id || 0);
  const compareItemIdDesc = (a, b) => Number(b?.item_id || 0) - Number(a?.item_id || 0);

  sorted.sort((a, b) => {
    if (sortBy === "price_desc") {
      return (
        (getPriceDollars(b) ?? -Infinity) - (getPriceDollars(a) ?? -Infinity) ||
        compareItemIdDesc(a, b)
      );
    }

    if (sortBy === "price_asc") {
      return (
        (getPriceDollars(a) ?? Infinity) - (getPriceDollars(b) ?? Infinity) ||
        compareItemIdAsc(a, b)
      );
    }

    if (sortBy === "name_asc") {
      return String(a?.name || "").localeCompare(String(b?.name || "")) || compareItemIdAsc(a, b);
    }

    if (sortBy === "name_desc") {
      return String(b?.name || "").localeCompare(String(a?.name || "")) || compareItemIdDesc(a, b);
    }

    if (sortBy === "item_id_asc") {
      return compareItemIdAsc(a, b);
    }

    return compareItemIdDesc(a, b);
  });

  return sorted;
}
